import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Snackbar,
  Stack,
  Tooltip
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import {
  useOpenerState,
  useStartOpener,
  useStopOpener,
  useClearOpenerPanicLockout,
  useBootOpenerOrchestrator,
  useShutdownOpenerOrchestrator,
} from '../../api/useEntry';
import { useDomainPresence } from 'src/Common/hooks/useDomainPresence';
import { useUpdates } from 'src/Modules/Arbitrage/context/updatesContext';
import { VenueMaintenanceControl } from '../shared/VenueMaintenanceControl';
import {
  JobProgressStepper,
  JobResultContent,
  buildJobResultSnack,
  JobResultSnack
} from 'src/Common/components/JobProgressStepper/JobProgressStepper';

/**
 * Mirror of `StopStartUnwinderButtons` but for the position-opener. The opener
 * has no separate /orders endpoint; the opener's panic endpoint batch-cancels
 * every active maker BUY across all rules before scaling down.
 */
export const StopStartOpenerButtons = () => {
  const { data: state } = useOpenerState();
  const stopMutation = useStopOpener();
  const startMutation = useStartOpener();
  const clearLockoutMutation = useClearOpenerPanicLockout();
  const bootOrchestratorMutation = useBootOpenerOrchestrator();
  const shutdownOrchestratorMutation = useShutdownOpenerOrchestrator();
  const [confirmStopOpen, setConfirmStopOpen] = useState(false);
  const [confirmShutdownOpen, setConfirmShutdownOpen] = useState(false);

  // Whether the CONTROL POD itself is up — a different fact from `isRunning`
  // below, which is engine liveness. Read from `/api/system/all/state`, served
  // by ROOT: `useOpenerState` is served by the opener-orchestrator itself, so
  // it goes stale exactly when the pod is down and can never report that.
  const { get: getDomainPresence } = useDomainPresence();
  const controlPlaneBooted = getDomainPresence('opener').booted;

  // No data means the pod that SERVES this query is unreachable. Defaulting
  // to "running" is the safe read while the control plane is up (never offer
  // START against a live engine on a transient blip) — but a drained pod is
  // now the NORMAL post-STOP state, and there the same default reads "Live",
  // disables START and points Stop at a dead endpoint. When root positively
  // reports the control plane down, believe root.
  const isRunning = state?.isRunning ?? controlPlaneBooted !== false;
  // While a 24h panic lockout is armed, START / rule activation are refused by
  // the control plane — disable START and surface a clear-lockout control.
  const lockout = state?.panicLockout;
  // Same stale-snapshot correction as `isRunning` above, for the same reason.
  // A verified STOP arms the 24h lockout AND drains the pod, so the last
  // snapshot this query ever got says `armed: true` — and it can never be
  // refreshed, because the pod that serves it is gone. Trusting it would
  // disable START permanently in that session, and START is the one button
  // that boots the pod back.
  const isLocked = Boolean(lockout?.armed) && controlPlaneBooted !== false;
  const isBusy =
    stopMutation.isLoading ||
    startMutation.isLoading ||
    clearLockoutMutation.isLoading;

  const { currentJob } = useUpdates();
  const isMyJob = Boolean(
    currentJob &&
      currentJob.subsystem === 'opener' &&
      (currentJob.status === 'running' ||
        (currentJob.completedAt && Date.now() - currentJob.completedAt < 5000))
  );

  const [showJobResult, setShowJobResult] = useState(false);
  const prevJobRunning = useRef(false);
  useEffect(() => {
    if (isMyJob) {
      prevJobRunning.current = true;
      setShowJobResult(true);
    } else if (prevJobRunning.current && !isMyJob) {
      prevJobRunning.current = false;
      const timer = setTimeout(() => setShowJobResult(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isMyJob]);

  const showProgress = (isMyJob || showJobResult) && currentJob;

  const stopRunning =
    isMyJob && currentJob?.type === 'stop' && currentJob?.status === 'running';
  const startRunning =
    isMyJob && currentJob?.type === 'start' && currentJob?.status === 'running';

  // Compact status chip + transient result snackbar so the operator always
  // has an at-a-glance signal even if the stepper popover lags or dismisses.
  // The stepper depends on the system-job poll loop (every 2s) seeded by the
  // mutation; `useOpenerState` polls /opener/state every 3s independently
  // and is the source of truth for "did the pod actually scale down".
  const status: 'running' | 'stopping' | 'starting' | 'stopped' = stopRunning
    ? 'stopping'
    : startRunning
      ? 'starting'
      : isRunning
        ? 'running'
        : 'stopped';

  // The wire reports LIVENESS only (engines up), never "domain enabled" —
  // domain START opens the gate and groups bring the engines up. Labelling that
  // "Stopped" read as a failed start; say what is actually being measured.
  const groupEngines = state?.groupEnginesRunning;
  const statusLabel = {
    running:
      typeof groupEngines === 'number' && groupEngines > 0
        ? `Live · ${groupEngines} group${groupEngines === 1 ? '' : 's'}`
        : 'Live',
    stopping: 'Stopping…',
    starting: 'Starting…',
    stopped: 'No groups running'
  }[status];

  const statusColor: 'success' | 'warning' | 'info' | 'default' = {
    running: 'success',
    stopping: 'warning',
    starting: 'info',
    stopped: 'default'
  }[status] as 'success' | 'warning' | 'info' | 'default';

  const [resultSnack, setResultSnack] = useState<JobResultSnack | null>(null);

  // Watch for job completion AFTER it was mine. The popover already shows
  // step-by-step progress; this is the "did it actually finish" toast that
  // survives even if the popover auto-dismisses or the user has navigated.
  const prevStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevStatusRef.current;
    if (
      currentJob &&
      currentJob.subsystem === 'opener' &&
      prev === 'running' &&
      (currentJob.status === 'completed' || currentJob.status === 'failed')
    ) {
      setResultSnack(buildJobResultSnack('Opener', currentJob));
    }
    prevStatusRef.current = currentJob?.status;
  }, [currentJob]);

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <VenueMaintenanceControl />
          <Tooltip
            title={
              status === 'stopping'
                ? 'Opener is stopping — pod is being scaled to 0'
                : status === 'starting'
                  ? 'Opener is starting — waiting for pod Ready'
                  : status === 'running'
                    ? `Opener pod running${
                        typeof state?.replicas === 'number'
                          ? ` (${state.replicas} replica${state.replicas === 1 ? '' : 's'})`
                          : ''
                      }`
                    : 'No group engines are up. START OPENER opens the gate; ' +
                      'a group comes up only when you start it from its own ' +
                      'Start button.' +
                      (state?.lastStopAt
                        ? ` Last stop ${new Date(state.lastStopAt).toLocaleTimeString()}.`
                        : '')
            }
            placement="top"
          >
            <Chip
              size="small"
              label={statusLabel}
              color={statusColor}
              variant={status === 'stopped' ? 'outlined' : 'filled'}
              icon={
                status === 'stopping' || status === 'starting' ? (
                  <CircularProgress size={10} color="inherit" sx={{ ml: 0.5 }} />
                ) : undefined
              }
              sx={{ fontSize: '0.65rem', height: 20, mr: 0.25 }}
            />
          </Tooltip>
          <Tooltip
            title={
              stopRunning
                ? 'Stopping opener…'
                : !isRunning
                  ? 'Opener already stopped'
                  : 'Detiene el opener: cancela órdenes en Kalshi y Polymarket, ' +
                    'pausa las reglas, arma el lockout de 24h y libera el ' +
                    'control plane. ' +
                    'OJO: las órdenes de la UI / proxy-wallet de Polymarket NO se cancelan.'
            }
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={
                  stopMutation.isLoading || stopRunning ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PowerSettingsNewIcon />
                  )
                }
                onClick={() => setConfirmStopOpen(true)}
                disabled={isBusy || !isRunning || Boolean(isMyJob && currentJob?.status === 'running')}
                sx={{ fontSize: '0.7rem' }}
              >
                {stopRunning ? 'Stopping' : 'Stop Opener'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              isLocked
                ? `Under 24h panic lockout${
                    lockout?.expiresAt
                      ? ` until ${new Date(lockout.expiresAt).toLocaleString()}`
                      : ''
                  } — clear the lockout to start`
                : startRunning
                  ? 'Starting opener…'
                  : isRunning
                    ? 'Opener already running'
                    : 'Start position-opener instance'
            }
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={
                  startMutation.isLoading || startRunning ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={() => startMutation.mutate()}
                disabled={isBusy || isRunning || isLocked || Boolean(isMyJob && currentJob?.status === 'running')}
                sx={{ fontSize: '0.7rem' }}
              >
                {startRunning ? 'Starting' : 'Start Opener'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              controlPlaneBooted === null
                ? 'No se pudo leer el estado del opener-orchestrator.'
                : controlPlaneBooted
                  ? 'El opener-orchestrator (control plane) está ARRIBA. Sirve ' +
                    'el CRUD de reglas y las rutas de on/off. Independiente de ' +
                    'que haya grupos operando.'
                  : 'El opener-orchestrator (control plane) está ABAJO. ' +
                    'Booteálo para editar reglas; START lo levanta solo.'
            }
            placement="top"
          >
            <Chip
              size="small"
              label={
                controlPlaneBooted === null
                  ? 'Control plane: ?'
                  : controlPlaneBooted
                    ? 'Control plane: UP'
                    : 'Control plane: DOWN'
              }
              color={controlPlaneBooted ? 'info' : 'default'}
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20, mr: 0.25 }}
            />
          </Tooltip>
          {isLocked && (
            <Tooltip
              title={
                `Opener is under a 24h panic lockout` +
                `${lockout?.armedAt ? ` (armed ${new Date(lockout.armedAt).toLocaleString()})` : ''}` +
                `. Clearing it re-enables Start + rule activation.`
              }
              placement="top"
            >
              <span>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={
                    clearLockoutMutation.isLoading ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : (
                      <LockOpenIcon />
                    )
                  }
                  onClick={() => clearLockoutMutation.mutate()}
                  disabled={clearLockoutMutation.isLoading}
                  sx={{ fontSize: '0.7rem' }}
                >
                  Clear lockout
                </Button>
              </span>
            </Tooltip>
          )}
          {/*
            The manual control-plane pair the unwinder has always had. Needed
            here as more than symmetry: `useOpenerState` is served BY this pod,
            so once it is down the header's own state freezes and there is no
            other way back — START does boot it, but START is disabled off that
            frozen state. This is the escape hatch.
          */}
          <Tooltip
            title={
              'Bootea el opener-orchestrator (control plane). Hace falta para ' +
              'editar reglas y para las rutas de on/off — START lo levanta ' +
              'solo, esto es para levantarlo sin arrancar el trading.'
            }
            placement="top"
          >
            <span>
              <Button
                variant="text"
                color="info"
                size="small"
                startIcon={
                  bootOrchestratorMutation.isLoading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <CloudSyncIcon />
                  )
                }
                onClick={() => bootOrchestratorMutation.mutate()}
                disabled={bootOrchestratorMutation.isLoading}
                sx={{ fontSize: '0.7rem' }}
              >
                Boot control plane
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              'Apaga el opener-orchestrator (control plane). El CRUD de reglas ' +
              'deja de responder hasta bootearlo de nuevo. No toca el trading: ' +
              'para eso está Stop Opener.'
            }
            placement="top"
          >
            <span>
              <Button
                variant="text"
                color="inherit"
                size="small"
                startIcon={
                  shutdownOrchestratorMutation.isLoading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <CloudOffIcon />
                  )
                }
                onClick={() => setConfirmShutdownOpen(true)}
                disabled={shutdownOrchestratorMutation.isLoading}
                sx={{ fontSize: '0.7rem' }}
              >
                Shutdown control plane
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {isLocked && (
          <Box sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
              color="error"
              variant="outlined"
              label={`24h panic lockout${
                lockout?.expiresAt
                  ? ` — expires ${new Date(lockout.expiresAt).toLocaleTimeString()}`
                  : ''
              }`}
              sx={{ fontSize: '0.6rem', height: 18 }}
            />
          </Box>
        )}

        {showProgress && currentJob && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              right: 0,
              mt: 1,
              zIndex: 1400
            }}
          >
            <Paper elevation={6} sx={{ p: 1.5, minWidth: 280, maxWidth: 340, bgcolor: 'background.paper' }}>
              <JobProgressStepper job={currentJob} label="Opener" />
            </Paper>
          </Box>
        )}
      </Box>

      <Snackbar
        open={Boolean(resultSnack)}
        autoHideDuration={resultSnack?.remaining ? null : 8000}
        onClose={() => setResultSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {resultSnack ? (
          <Alert
            severity={resultSnack.kind}
            onClose={() => setResultSnack(null)}
            sx={{ fontSize: '0.8rem', maxWidth: 360 }}
          >
            <JobResultContent snack={resultSnack} />
          </Alert>
        ) : undefined}
      </Snackbar>

      {/*
        Boot/shutdown failures are NOT surfaced here: the app-wide
        `MutationCache.onError` already toasts them with copy keyed on
        `opener:boot-orchestrator` / `opener:shutdown-orchestrator` (plus a
        Sentry capture). A second Snackbar would both duplicate that message
        and collide with the job-result one below — same anchorOrigin, and MUI
        does not stack independent Snackbars.
      */}
      <Dialog
        open={confirmStopOpen}
        onClose={() => setConfirmStopOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Stop Opener</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Esto va a:
            <ul style={{ margin: '4px 0', paddingLeft: '1.2em' }}>
              <li>Cancelar todas las órdenes abiertas en Kalshi y Polymarket.</li>
              <li>Pausar todas las reglas del opener.</li>
              <li>Armar un lockout de 24h (bloquea Start hasta limpiarlo a mano).</li>
              <li>
                Liberar el control plane: el opener-orchestrator queda
                apagable, y root lo recupera solo (~30s) salvo que algo más lo
                esté sosteniendo — predictions prendido, un grupo todavía
                arriba, o un boot manual sin cerrar.
              </li>
            </ul>
            OJO: las órdenes de la UI / proxy-wallet de Polymarket NO se cancelan.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStopOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmStopOpen(false);
              stopMutation.mutate();
            }}
            color="warning"
            variant="contained"
            startIcon={<PowerSettingsNewIcon />}
          >
            Stop Opener
          </Button>
        </DialogActions>
      </Dialog>

      {/*
        Confirmed for the same reason Stop Opener is: this drains the pod that
        serves the page you are on, and the backend releases EVERY claim on it
        — including the predictions-start one, which belongs to a lifecycle
        this screen knows nothing about.
      */}
      <Dialog
        open={confirmShutdownOpen}
        onClose={() => setConfirmShutdownOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Shutdown control plane</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Apaga el opener-orchestrator. No toca el trading — para eso está
            Stop Opener.
            <ul style={{ margin: '4px 0', paddingLeft: '1.2em' }}>
              <li>El CRUD de reglas deja de responder hasta bootearlo.</li>
              <li>
                Libera TODOS los claims sobre el pod, incluido el de un
                predictions-start en curso.
              </li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmShutdownOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmShutdownOpen(false);
              shutdownOrchestratorMutation.mutate();
            }}
            color="warning"
            variant="contained"
            startIcon={<CloudOffIcon />}
          >
            Shutdown
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
